import { useEffect, useRef, useState } from "react";
import { useGame } from "../../net/gameClient";
import type { GameMode, Snapshot } from "../../../shared/types";
import { Crescent, Confetti, FlourDust, Logo, OptGlyph, OvenDial, Steam } from "../../components/motifs";
import { Ico } from "../../components/shell";
import { OvenTimer, Phone, PinCells } from "../../components/primitives";
import { api, storedPlayer, rememberPlayer, forgetPlayer } from "../../api";
import { mediaUrl } from "../../../shared/format";

// Fun avatar emojis a baker can pick on join — shown next to their name all game.
const PLAYER_EMOJIS = [
  "🦊", "🐼", "🐶", "🐱", "🦁", "🐯", "🐸", "🐵",
  "🦄", "🐝", "🐢", "🐙", "🐧", "🦉", "🐰", "🦖",
  "😀", "😎", "🤓", "🥳", "🤖", "👻", "🐲", "🦋",
  "🍓", "🍕", "🍩", "🧁", "🌟", "🔥", "🌈", "🚀",
];
const randomPlayerEmoji = () => PLAYER_EMOJIS[Math.floor(Math.random() * PLAYER_EMOJIS.length)];

export default function Player({ pinParam }: { pinParam?: string }) {
  // A /play/:pin link targets that game; otherwise, if we have a saved session
  // (and no conflicting link), we may resume it — but only after checking it's
  // still live (see the validation effect below). We never auto-rejoin a game
  // that's already finished, so a refresh after the podium lands on a fresh
  // join screen rather than replaying the last result.
  const urlPin = (pinParam ?? "").replace(/\D/g, "").slice(0, 6);
  const saved = (() => {
    const s = storedPlayer();
    return s && (!urlPin || urlPin === s.pin) ? s : null;
  })();
  const [pin, setPin] = useState(saved?.pin ?? urlPin);
  const [name, setName] = useState(saved?.name ?? "");
  const [emoji, setEmoji] = useState<string>(saved?.emoji ?? randomPlayerEmoji());
  const [active, setActive] = useState<string | null>(null);
  const [validating, setValidating] = useState(!!saved);
  const [joinErr, setJoinErr] = useState("");
  const [checking, setChecking] = useState(false);
  const [peek, setPeek] = useState<{ quizTitle: string; hostName?: string; mode?: GameMode } | null>(null);
  const joinSent = useRef(false);
  const game = useGame(active, "player");

  // Validate a saved session before auto-rejoining: resume only a game that's
  // still live. A finished game (podium/ended) — or one the server has forgotten
  // — is dropped, so reopening /play after a quiz wraps up shows a clean join
  // screen ready for the next PIN instead of replaying the old result.
  useEffect(() => {
    if (!saved) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.peekSession(saved.pin);
        if (cancelled) return;
        if (r.found && r.phase !== "podium" && r.phase !== "ended") setActive(saved.pin);
        else forgetPlayer();
      } catch {
        // Network hiccup — try to resume anyway; the socket will reject a dead room.
        if (!cancelled) setActive(saved.pin);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefetch the session (quiz title + host name) once a full PIN is entered,
  // so the join screen can greet players with who's hosting before they tap in.
  useEffect(() => {
    if (active || pin.length !== 6) {
      setPeek(null);
      return;
    }
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const r = await api.peekSession(pin);
        if (!cancelled) setPeek(r.joinable ? { quizTitle: r.quizTitle, hostName: r.hostName, mode: r.mode } : null);
      } catch {
        /* network hiccup — ignore, the join attempt will surface errors */
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [pin, active]);

  // (Re)send join whenever the socket (re)connects.
  useEffect(() => {
    if (active && game.connected && name.trim()) {
      game.join(name.trim(), emoji, saved?.token);
      joinSent.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, game.connected]);

  // Surface a server-side rejection (bad PIN / room not live) and let them retry.
  // A rejection means our saved session is dead, so forget it (no resume loop).
  useEffect(() => {
    if (game.error) {
      setJoinErr(game.error);
      setActive(null);
      forgetPlayer();
    }
  }, [game.error]);

  async function attemptJoin() {
    if (pin.length !== 6 || !name.trim() || checking) return;
    setJoinErr("");
    setChecking(true);
    try {
      const peek = await api.peekSession(pin);
      if (!peek.joinable) {
        setJoinErr(
          peek.found && peek.phase === "podium"
            ? "That game has already finished — check with your host."
            : peek.found
              ? "That game isn't open to join right now — check the big screen."
              : "No live game with that PIN — check the big screen.",
        );
        setChecking(false);
        return;
      }
    } catch {
      /* network hiccup — let the socket try anyway */
    }
    setChecking(false);
    setActive(pin);
  }

  const snap = game.snapshot;
  const joined = !!snap?.you;

  // Persist the live session so a refresh auto-rejoins (server resumes score by
  // nickname) — but only while the game is in progress. Once it reaches the
  // podium/ended, forget it so a later refresh or reopen starts fresh instead of
  // dropping back onto the finished game's result.
  useEffect(() => {
    const over = snap?.phase === "podium" || snap?.phase === "ended";
    // Persist the per-player token too, so a refresh re-binds to our name/score.
    if (joined && active && !over) rememberPlayer(active, name, emoji, game.playerToken ?? saved?.token);
    else if (over) forgetPlayer();
  }, [joined, active, name, emoji, snap?.phase, game.playerToken]);

  if (!active || !joined) {
    return (
      <JoinForm
        pin={pin}
        name={name}
        setPin={(v) => {
          setPin(v);
          setJoinErr("");
        }}
        setName={setName}
        emoji={emoji}
        setEmoji={setEmoji}
        waiting={validating || (!!active && !joined) || checking}
        error={joinErr}
        quizTitle={game.quizTitle || peek?.quizTitle || ""}
        hostName={game.hostName || peek?.hostName || ""}
        teamsMode={peek?.mode === "teams"}
        onSubmit={attemptJoin}
      />
    );
  }

  if (snap!.paused) return <PlayerPaused />;

  switch (snap!.phase) {
    case "question":
      return <PlayerAnswer snap={snap!} onAnswer={game.sendAnswer} />;
    case "reveal":
      return <PlayerFeedback snap={snap!} />;
    case "leaderboard":
      return <PlayerStanding snap={snap!} />;
    case "podium":
      return <PlayerPodium snap={snap!} />;
    case "lobby":
    default:
      return <PlayerLobby snap={snap!} />;
  }
}

/* ───────────────────────── JOIN ───────────────────────── */
function JoinForm({
  pin,
  name,
  setPin,
  setName,
  emoji,
  setEmoji,
  waiting,
  error,
  quizTitle,
  hostName,
  teamsMode,
  onSubmit,
}: {
  pin: string;
  name: string;
  setPin: (v: string) => void;
  setName: (v: string) => void;
  emoji: string;
  setEmoji: (v: string) => void;
  waiting: boolean;
  error?: string;
  quizTitle: string;
  hostName: string;
  teamsMode: boolean;
  onSubmit: () => void;
}) {
  return (
    <Phone>
      <div className="bk-join">
        <div className="bk-join__logo">
          <Logo />
        </div>

        <div className="bk-join__hero">
          {/* In teams mode the team carries the emoji, so players don't pick one. */}
          <div className="bk-join__avatar" style={{ animation: "bk-float 4s ease-in-out infinite" }}>{teamsMode ? "🥐" : emoji}</div>
          <h1 className="bk-join__title">Ready to rise?</h1>
          <p className="bk-join__intro">
            {waiting ? "Knocking on the bakery door…" : "Pop in the game PIN on the screen to join the bake."}
          </p>
        </div>

        <div className="bk-join__fields">
          <div>
            <div className="bk-label" style={{ textAlign: "center", marginBottom: 10 }}>
              GAME PIN
            </div>
            {/* The 6 cells are the input: a transparent field overlays them so
                tapping anywhere focuses it and brings up the numeric keypad. */}
            <label style={{ display: "block", position: "relative", cursor: "text" }}>
              <PinCells value={pin} />
              <input
                className="mono"
                inputMode="numeric"
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                aria-label="Game PIN"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  border: "none",
                  background: "transparent",
                  color: "transparent",
                  caretColor: "transparent",
                  fontSize: 16,
                  cursor: "text",
                }}
              />
            </label>
          </div>
          <div className="bk-field">
            <label className="bk-label">Your name</label>
            <input
              className="bk-input"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 24))}
              placeholder="Alex"
              style={{ fontSize: 16, padding: "14px 16px" }}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmit();
              }}
            />
            {teamsMode && (
              <div style={{ fontSize: 12, color: "var(--ink-faint)", fontWeight: 700, marginTop: 6 }}>
                You'll be popped into a team when you join. 👥
              </div>
            )}
          </div>

          {!teamsMode && (
            <div className="bk-field">
              <label className="bk-label">Pick your emoji</label>
              <div className="bk-join__emojis">
                {PLAYER_EMOJIS.map((e) => {
                  const on = e === emoji;
                  return (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setEmoji(e)}
                      aria-label={`Choose ${e}`}
                      style={{
                        border: on ? "2px solid var(--red)" : "1px solid var(--line)",
                        background: on ? "var(--red-tint)" : "var(--surface)",
                      }}
                    >
                      {e}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div
            className="bk-chip bk-chip--red"
            style={{ justifyContent: "center", padding: "10px 14px", fontWeight: 700, marginTop: 18 }}
          >
            {error}
          </div>
        )}
        <button
          className="bk-btn bk-btn--primary bk-btn--xl bk-btn--block"
          style={{ marginTop: error ? 10 : undefined }}
          disabled={pin.length !== 6 || !name.trim() || waiting}
          onClick={onSubmit}
        >
          {waiting ? "Joining…" : "Let's bake →"}
        </button>
        <div className="bk-join__footer">
          {hostName ? `Hosted by ${hostName}` : quizTitle ? quizTitle : "Pop in the game PIN to join the bake"}
        </div>
      </div>
    </Phone>
  );
}

/* ───────────────────────── LOBBY ───────────────────────── */
function PlayerLobby({ snap }: { snap: Snapshot }) {
  const team = snap.you!.team;
  return (
    <Phone bg="var(--red)" dark grain={false}>
      <div style={{ position: "absolute", inset: 0 }}>
        <FlourDust count={16} />
      </div>
      <div
        style={{
          padding: "8px 30px 30px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100dvh",
          color: "#fff",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
          <Logo light />
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 22 }}>
          <div style={{ position: "relative", display: "grid", placeItems: "center" }}>
            <div style={{ position: "absolute", left: "50%", top: -26, transform: "translateX(-50%)" }}>
              <Steam count={3} color="rgba(255,255,255,.8)" height={26} />
            </div>
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: 36,
                background: "rgba(255,255,255,.14)",
                display: "grid",
                placeItems: "center",
                boxShadow: "inset 0 0 0 2px rgba(255,255,255,.25)",
              }}
            >
              <OvenDial size={70} color="#fff" spin />
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 700, opacity: 0.85, letterSpacing: ".04em" }}>YOU'RE IN!</div>
            {team ? (
              <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-.03em", marginTop: 4 }}>
                You're on
                <br />
                {team.emoji} {team.name}
              </h1>
            ) : (
              <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-.03em", marginTop: 4 }}>
                Warming up
                <br />
                the oven…
              </h1>
            )}
          </div>
          <div
            style={{
              background: "rgba(255,255,255,.16)",
              borderRadius: 999,
              padding: "10px 22px",
              fontWeight: 800,
              fontSize: 17,
              display: "flex",
              alignItems: "center",
              gap: 9,
            }}
          >
            {snap.you!.emoji ? <span style={{ fontSize: 20 }}>{snap.you!.emoji}</span> : <Crescent size={20} color="#fff" />} {snap.you!.nickname}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, opacity: 0.8 }}>
            {team
              ? `${snap.playerCount} ${snap.playerCount === 1 ? "baker" : "bakers"} baking · hold tight`
              : `${snap.playerCount} ${snap.playerCount === 1 ? "team" : "teams"} baking · hold tight`}
          </div>
        </div>

        <div style={{ display: "flex", gap: 7 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{ width: 9, height: 9, borderRadius: 9, background: "#fff", animation: "bk-pulse 1.2s ease-in-out infinite", animationDelay: `${i * 0.18}s` }}
            />
          ))}
        </div>
      </div>
    </Phone>
  );
}

/* ───────────────────────── PAUSED (host stepped away) ───────────────────────── */
function PlayerPaused() {
  return (
    <Phone>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", flex: 1, padding: 30, gap: 18 }}>
        <div style={{ width: 100, height: 100, borderRadius: 30, background: "var(--gold)", display: "grid", placeItems: "center", boxShadow: "var(--sh-gold)" }}>
          <OvenDial size={58} color="#fff" />
        </div>
        <h1 style={{ fontSize: 27, fontWeight: 800, letterSpacing: "-.02em" }}>Just a moment…</h1>
        <p style={{ color: "var(--ink-soft)", fontWeight: 600, maxWidth: 260 }}>
          Paused while your host steps back to the screen — hang tight, the bake will pick up right where it left off. 🥐
        </p>
      </div>
    </Phone>
  );
}

/* ───────────────────────── ANSWER ───────────────────────── */
function PlayerAnswer({ snap, onAnswer }: { snap: Snapshot; onAnswer: (ids: string[]) => void }) {
  const q = snap.question!;
  const multi = q.type === "multi";
  const [sel, setSel] = useState<string[]>([]);
  const answered = snap.you!.answered;

  if (answered) {
    return (
      <Phone>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", flex: 1, padding: 30, gap: 18 }}>
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", left: "50%", top: -26, transform: "translateX(-50%)" }}>
              <Steam count={3} color="var(--crust-soft)" height={26} />
            </div>
            <div style={{ width: 100, height: 100, borderRadius: 30, background: "var(--gold)", display: "grid", placeItems: "center", boxShadow: "var(--sh-gold)" }}>
              <OvenDial size={58} color="#fff" spin />
            </div>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-.02em" }}>Answer locked in!</h1>
          <p style={{ color: "var(--ink-soft)", fontWeight: 600 }}>In the oven — hold tight for the reveal. 🔥</p>
        </div>
      </Phone>
    );
  }

  return (
    <Phone>
      <div style={{ padding: "6px 18px 18px", display: "flex", flexDirection: "column", height: "100dvh", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 6px" }}>
          <span className="bk-chip bk-chip--red" style={{ fontSize: 12.5 }}>
            <span className="mono">Q{snap.index + 1}</span>/{snap.total}
          </span>
          <span style={{ fontWeight: 800, fontSize: 13, color: "var(--ink-soft)", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {snap.you!.team
              ? `${snap.you!.team.emoji} ${snap.you!.team.name}`
              : `${snap.you!.emoji ? snap.you!.emoji + " " : ""}${snap.you!.nickname}`}
          </span>
          <OvenTimer size={50} stroke={5} endsAt={q.endsAt} timeLimit={q.timeLimit} />
        </div>

        <div className="bk-card" style={{ padding: "16px 18px", marginTop: 12, textAlign: "center" }}>
          {q.eyebrow && (
            <div className="bk-eyebrow bk-eyebrow--red" style={{ fontSize: 10 }}>
              {q.eyebrow}
            </div>
          )}
          {/* Host chose "answers only" — show just the eyebrow (type/points), no prompt. */}
          {!snap.hideQuestion && (
            <>
              <h2 style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-.02em", marginTop: 6, lineHeight: 1.2 }}>{q.prompt}</h2>
              {q.imageKey && (
                <img src={mediaUrl(q.imageKey)} alt="" style={{ width: "100%", maxHeight: 150, objectFit: "cover", borderRadius: 12, marginTop: 10 }} />
              )}
            </>
          )}
          {multi && <div style={{ fontSize: 12, color: "var(--ink-faint)", fontWeight: 700, marginTop: 6 }}>Pick all that apply, then lock it in.</div>}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 11, marginTop: 12, flex: 1, minHeight: 0 }}>
          {q.options.map((o) => {
            const on = sel.includes(o.id);
            return (
              <button
                key={o.id}
                className={`bk-opt bk-opt--${o.key}${multi && !on ? " bk-opt--dim" : ""}`}
                style={{ flex: 1, minHeight: 0, fontSize: 18, padding: "0 20px", textAlign: "left", overflow: "hidden" }}
                onClick={() => {
                  if (multi) setSel((s) => (s.includes(o.id) ? s.filter((x) => x !== o.id) : [...s, o.id]));
                  else onAnswer([o.id]);
                }}
              >
                <span className="bk-opt__glyph">
                  <OptGlyph kind={o.key} size={24} />
                </span>
                {o.imageKey && (
                  <img src={mediaUrl(o.imageKey)} alt="" style={{ height: 36, width: 36, objectFit: "cover", borderRadius: 8 }} />
                )}
                <span style={{ flex: 1 }}>{o.text}</span>
                {multi && on && <Ico name="check" size={20} />}
              </button>
            );
          })}
        </div>

        {multi ? (
          <button
            className="bk-btn bk-btn--primary bk-btn--lg bk-btn--block"
            style={{ marginTop: 12 }}
            disabled={sel.length === 0}
            onClick={() => onAnswer(sel)}
          >
            Lock it in →
          </button>
        ) : (
          <div style={{ textAlign: "center", fontSize: 12.5, color: "var(--ink-faint)", fontWeight: 700, marginTop: 12 }}>
            Tap your answer — fastest correct teams rise highest 🔥
          </div>
        )}
      </div>
    </Phone>
  );
}

/* ───────────────────────── FEEDBACK ───────────────────────── */
function PlayerFeedback({ snap }: { snap: Snapshot }) {
  const result = snap.you!.result;
  if (result?.correct) return <PlayerCorrect snap={snap} />;
  // A partially-right multi answer earned points — celebrate it, don't call it wrong.
  return <PlayerIncorrect snap={snap} partial={!!result?.partial} />;
}

function PlayerCorrect({ snap }: { snap: Snapshot }) {
  const r = snap.you!.result!;
  return (
    <Phone bg="linear-gradient(160deg, #6E8C45, #4d7338)" dark grain={false}>
      <div style={{ position: "absolute", inset: 0 }}>
        <Confetti count={32} colors={["#fff", "#F7E7C4", "#D89A36"]} />
      </div>
      <div
        style={{
          padding: 30,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100dvh",
          color: "#fff",
          position: "relative",
          zIndex: 2,
          textAlign: "center",
        }}
      >
        <div style={{ width: 108, height: 108, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "grid", placeItems: "center", animation: "bk-pulse 1.6s ease-in-out infinite" }}>
          <div style={{ width: 78, height: 78, borderRadius: "50%", background: "#fff", display: "grid", placeItems: "center", color: "#4d7338" }}>
            <Ico name="check" size={44} />
          </div>
        </div>
        <h1 style={{ fontSize: 38, fontWeight: 800, letterSpacing: "-.03em", marginTop: 24 }}>Correct!</h1>
        <p style={{ fontSize: 18, fontWeight: 700, opacity: 0.92, marginTop: 4 }}>Nicely baked. 🥐</p>

        <div style={{ background: "rgba(255,255,255,.16)", borderRadius: 20, padding: "18px 26px", marginTop: 28, width: "100%" }}>
          <div className="mono" style={{ fontSize: 44, fontWeight: 700 }}>+{r.pointsEarned}</div>
          <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.85, letterSpacing: ".06em" }}>
            POINTS · ANSWERED IN {(r.elapsedMs / 1000).toFixed(1)}s
          </div>
          {r.streakBonus > 0 && (
            <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6, color: "#F7E7C4" }}>
              🔥 includes +{r.streakBonus} streak bonus
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap", justifyContent: "center" }}>
          {snap.you!.streak > 1 && (
            <span className="bk-chip" style={{ background: "rgba(255,255,255,.16)", color: "#fff", border: "none", fontSize: 13 }}>
              <Ico name="fire" size={14} /> {snap.you!.streak} in a row
            </span>
          )}
          <span className="bk-chip" style={{ background: "rgba(255,255,255,.16)", color: "#fff", border: "none", fontSize: 13 }}>
            ▲ {ordinal(snap.you!.rank)} · {snap.you!.team ? "team rising" : "rising"}
          </span>
          {snap.you!.team && (
            <span className="bk-chip" style={{ background: "rgba(255,255,255,.16)", color: "#fff", border: "none", fontSize: 13 }}>
              {snap.you!.team.emoji} {snap.you!.team.name}
            </span>
          )}
        </div>
        <div style={{ marginTop: "auto", fontSize: 14, fontWeight: 700, opacity: 0.8 }}>Eyes up — next question loading…</div>
      </div>
    </Phone>
  );
}

function PlayerIncorrect({ snap, partial = false }: { snap: Snapshot; partial?: boolean }) {
  const correctIds = new Set(snap.reveal?.correctIds ?? []);
  const correctOpts = (snap.question?.options ?? []).filter((o) => correctIds.has(o.id));
  const earned = snap.you!.result?.pointsEarned ?? 0;
  // A partial multi answer scored something — use a warmer amber treatment.
  const bg = partial
    ? "linear-gradient(160deg, #C8862A, #A8631F)"
    : "linear-gradient(160deg, var(--red-deep), var(--red))";
  return (
    <Phone bg={bg} dark grain={false}>
      <div
        style={{
          padding: 30,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100dvh",
          color: "#fff",
          textAlign: "center",
        }}
      >
        <div style={{ width: 108, height: 108, borderRadius: "50%", background: "rgba(0,0,0,.16)", display: "grid", placeItems: "center" }}>
          <OvenDial size={64} color="#fff" />
        </div>
        <h1 style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-.03em", marginTop: 24 }}>
          {partial ? "So close!" : "Not quite!"}
        </h1>
        <p style={{ fontSize: 18, fontWeight: 700, opacity: 0.92, marginTop: 4 }}>
          {partial ? `Partial credit — +${earned} pts 🥐` : "Back in the oven. 🔥"}
        </p>

        <div style={{ background: "rgba(255,255,255,.14)", borderRadius: 18, padding: "16px 22px", marginTop: 26, width: "100%" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, opacity: 0.8, letterSpacing: ".06em" }}>
            THE CORRECT ANSWER {correctOpts.length > 1 ? "WAS" : "WAS"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {correctOpts.map((o) => (
              <div key={o.id} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <span style={{ width: 26, height: 26, display: "grid", placeItems: "center" }}>
                  <OptGlyph kind={o.key} size={22} />
                </span>
                <span style={{ fontWeight: 800, fontSize: 18 }}>{o.text}</span>
              </div>
            ))}
          </div>
        </div>
        {snap.reveal?.chefNote && (
          <div className="bk-chalk" style={{ marginTop: 18, textAlign: "left", background: "rgba(0,0,0,.22)", boxShadow: "inset 0 0 0 2px rgba(255,255,255,.12)" }}>
            <b>Note:</b> {snap.reveal.chefNote}
          </div>
        )}
        <div style={{ marginTop: "auto", fontSize: 14, fontWeight: 700, opacity: 0.85 }}>Still in it — next one's yours 💪</div>
      </div>
    </Phone>
  );
}

/* ───────────────────────── BETWEEN-Q STANDING ───────────────────────── */
function PlayerStanding({ snap }: { snap: Snapshot }) {
  const you = snap.you!;
  const team = you.team;
  const myKey = team ? team.name : you.nickname; // entity key in the ranked list
  const name = team ? `${team.emoji} ${team.name}` : `${you.emoji ? you.emoji + " " : ""}${you.nickname}`;
  const score = team ? team.score : you.score;
  const mine = snap.players.find((p) => p.nickname === myKey);
  const move = mine?.move ?? "same";
  const moveLabel = move === "up" ? "▲ up the order" : move === "down" ? "▼ slipped back" : "holding steady";
  // Top of the board: the leaders + your own row (deduped if you're already up top).
  const list = [...snap.players.slice(0, 2)];
  if (mine && !list.includes(mine)) list.push(mine);

  return (
    <Phone>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "26px 22px", gap: 12 }}>
        <div className="bk-eyebrow bk-eyebrow--red" style={{ textAlign: "center" }}>
          AFTER QUESTION {snap.index + 1} OF {snap.total}
        </div>
        <div style={{ textAlign: "center" }}>
          <div className="mono" style={{ fontSize: 60, fontWeight: 700, color: "var(--red)", lineHeight: 1 }}>{ordinal(you.rank)}</div>
          <h1 style={{ fontSize: 23, fontWeight: 800, letterSpacing: "-.02em", marginTop: 6 }}>{name}</h1>
        </div>
        <div className="bk-card bk-card--warm" style={{ padding: "14px 18px", textAlign: "center" }}>
          <div className="mono" style={{ fontSize: 32, fontWeight: 700 }}>{score.toLocaleString()}</div>
          <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: ".06em", color: "var(--ink-soft)" }}>
            POINTS · {moveLabel.toUpperCase()}
          </div>
        </div>
        {you.streak >= 2 && (
          <div
            style={{
              alignSelf: "center",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              background: "linear-gradient(100deg, var(--gold), var(--caramel))",
              color: "#fff",
              borderRadius: 999,
              padding: "8px 15px",
              fontWeight: 800,
              fontSize: 14,
            }}
          >
            <Ico name="fire" size={15} /> {you.streak} in a row — keep it going
          </div>
        )}
        {team && (
          <div style={{ textAlign: "center", fontSize: 12.5, fontWeight: 700, color: "var(--ink-faint)" }}>
            You chipped in {you.score.toLocaleString()} pts for the team
          </div>
        )}
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
          <div className="bk-eyebrow" style={{ fontSize: 10.5 }}>TOP OF THE BAKERY</div>
          {list.map((p) => {
            const isMe = p.nickname === myKey;
            return (
              <div
                key={p.nickname}
                className="bk-card"
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", boxShadow: isMe ? "0 0 0 2px var(--gold)" : "var(--sh-sm)" }}
              >
                <span className="mono" style={{ color: "var(--ink-faint)", fontWeight: 700, width: 18 }}>{p.rank}</span>
                <span style={{ width: 24, height: 24, borderRadius: 7, background: p.color, color: "#fff", display: "grid", placeItems: "center", fontWeight: 800, fontSize: p.emoji ? 13 : 10, flex: "0 0 auto" }}>
                  {p.emoji || p.init}
                </span>
                <span style={{ fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.nickname}</span>
                <span className="mono tnum" style={{ marginLeft: "auto", fontWeight: 700, fontSize: 14 }}>{p.score.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
        <p style={{ textAlign: "center", color: "var(--ink-soft)", fontWeight: 600, fontSize: 13 }}>Eyes on the big screen — next question soon.</p>
      </div>
    </Phone>
  );
}

/* ───────────────────────── PODIUM (player view) ───────────────────────── */
function PlayerPodium({ snap }: { snap: Snapshot }) {
  const you = snap.you!;
  const team = you.team;
  const won = you.rank === 1;
  return (
    <Phone bg={won ? "linear-gradient(160deg, var(--gold), var(--gold-deep))" : "var(--cream)"} dark={won} grain={!won}>
      {won && (
        <div style={{ position: "absolute", inset: 0 }}>
          <Confetti count={36} />
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          flex: 1,
          padding: 30,
          gap: 12,
          position: "relative",
          zIndex: 2,
          color: won ? "#fff" : "var(--ink)",
        }}
      >
        <div className="mono" style={{ fontSize: 72, fontWeight: 700, lineHeight: 1 }}>{ordinal(you.rank)}</div>
        <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-.03em" }}>
          {won ? "BakeOff Champions!" : "That's a bake!"}
        </h1>
        <p style={{ fontWeight: 700, opacity: 0.9 }}>{team ? `${team.emoji} ${team.name}` : `${you.emoji ? you.emoji + " " : ""}${you.nickname}`}</p>
        <div className="mono" style={{ fontSize: 22, fontWeight: 700, opacity: 0.9 }}>
          {(team ? team.score : you.score).toLocaleString()} pts
        </div>
        <p style={{ fontWeight: 600, opacity: 0.85, marginTop: 8 }}>
          {won
            ? team
              ? "Top of the tray — what a team. 🥐"
              : "Top of the tray. Nicely done. 🥐"
            : "Great rise — there's always the next bake. 💪"}
        </p>
        {team && (
          <p style={{ fontWeight: 600, opacity: 0.75, fontSize: 13 }}>
            Your contribution: {you.score.toLocaleString()} pts
          </p>
        )}
      </div>
    </Phone>
  );
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}
